export type ApiResponse<T> = {
  jsonrpc: string
  result: T
  error?: {
    code: number
    message: string
  }
}

export type Country = {
  id: number
  display_name: string
}

export type CountryState = {
  id: number
  display_name: string
}

export type User = {
  uid: string
  name: string
  username: string
  websocket_worker_version?: string
  gender: 'male' | 'female'
  maqari_role: UserRole
  partner_display_name: string
  student_id?: number | Entity
  student_ids?: Array<number | Entity>
}

export enum UserRole {
  TEACHER = 'TEACHER',
  ADMIN = 'ADMIN',
  STUDENT = 'STUDENT'
}
export type Mosque = {
  id: number
  display_name: string
}

export type QuranSection = {
  id: number
  display_name: string
  type: 'memorize' | 'review'
}

export type QuranSectionLine = {
  id: number
  display_name: string
}

export type Student = {
  id: number
  name: string
  gender?: 'male' | 'female' | false
  todayEvaluation: any
  preEvaluation: any
  evaluations: any[]
  nationality_id: {
    id: number
    name: string
  }
  civil_registry: string
  date_of_birth: string
  education_type: string
  completed_noorani_qaida_study: boolean
  educational_stage: string
  mosque_id: {
    id: number
    name: string
  }
  student_parent: boolean
  student_parent_mobile: boolean
  address: string
  quranic_narratives_id?: Entity | { id: number; display_name: string } | string | number | false
  quranic_level?: Entity | { id: number; display_name: string } | string | number | false
  previous_memorization: number
  mobile: string
  last_memorization: string | boolean
  email: string | false
  student_strengths: boolean
  general_notes: boolean
  student_session_id: {
    id: number
    name: string
  }
  date_start_plan: string | boolean
  date_end_plan: string | boolean
  daily_recitation_plan_id: {
    id: number
    name: string
  }
  recitation_start_id: {
    id: number
    name: string
  }
  daily_revision_plan_id: {
    id: number
    name: string
  }
  revision_start_id: {
    id: number
    name: string
  }
  create_date: string
  current_plan_id: Entity
  last_memorization_surah_number: number
  last_memorization_verses: number
  today_plan?: TodayPlan
  plan_progress?: StudentPlanProgress

  mobile_whatsapp_verification: boolean
  state?: 'draft' | 'in_progress' | 'done' | 'cancel'
  active?: boolean
  required_session?: boolean
  group_day_id?:
    | number
    | {
        id: number
        name?: string
        display_name?: string
      }
  hand_on?:
    | {
        time: string
        event: 'hand_on'
      }
    | false
  call_event?:
    | {
        time: string
        event: 'call_ring' | 'call_started' | 'call_paused' | 'call_ended'
        session_call_id?: number | false
        room_type?: 'private' | 'group'
      }
    | false
  partner_id?: Entity | false
  user_id?:
    | {
        login_date?: string | false
        im_status?: 'online' | 'offline' | string
      }
    | false
  stage_id?: {
    id: number
    name: string
    code?: string
  }
}

export type Session = {
  id: number
  name: string
  active?: boolean
  gender: 'male' | 'female' | false
  mosque_id: {
    id: number
    name: string
  }
  period: string

  teacher_id: {
    id: number
    name: string
  }
  specified_time: boolean
  session_time_from: number
  session_time_to: number
  specialist_teacher_id: boolean
  session_admin_id?:
    | false
    | {
        id: number
        name: string
      }
  daily_evaluation_sections: string
  is_started: boolean
  request_type_id: {
    id: number
    name: string
  }
  day_ids?: Array<{
    id: number
    name: string
    weekday: number
  }>
  group_day_id?:
    | number
    | {
        id: number
        name?: string
        display_name?: string
      }
  daily_recitation_plan_id?:
    | number
    | {
        id: number
        name?: string
        display_name?: string
      }
    | false
  recitation_start_id?:
    | number
    | {
        id: number
        name?: string
        display_name?: string
      }
    | false
  daily_revision_plan_id?:
    | number
    | {
        id: number
        name?: string
        display_name?: string
      }
    | false
  revision_start_id?:
    | number
    | {
        id: number
        name?: string
        display_name?: string
      }
    | false
  student_ids: Array<{
    id: number
    name: string
  }>
  create_date: string
  agora_room: string | false
  agora_token: string | false
  // The compatibility session endpoint returns the current durable call-room
  // snapshot. It is deliberately token-free; each participant renews their
  // own credentials before joining.
  current_room_id?:
    | {
        id: number
        room_id: number
        session_id: number
        start_date: string
        room_type: 'private' | 'group'
        status: 'ringing' | 'active' | 'ended' | 'cancelled'
      }
    | false
}

export interface Evaluation extends Object {
  id: number
  session_id: Session
  student_id: Student
  daily_evaluation_sections: string
  presence_type: 'present' | 'absent' | 'late' | 'excused'
  hearing_not_done: boolean
  absence_reason: boolean | string
  late_reason: boolean | string
  late_duration: number
  register_infraction: boolean
  infraction: boolean
  final_degree: boolean | number
  date: string
  stage_id: boolean | number
  character_exits: boolean
  revelation: boolean | number
  intonation: boolean | number
  teacher_notes: boolean | string
  parent_notes: boolean | string
  request_type_id: boolean | number
  gender: boolean | string
  verse_memorize_from: number
  verse_memorize_to: number
  total_memorize_line: number
  total_memorize_page: number
  memorize_school_error_ids: number[]
  surah_review_from_id: {
    name: string
    id: number
  }
  verse_review_from: number
  surah_review_to_id: {
    name: string
    id: number
  }
  verse_review_to: number
  total_review_line: number
  total_review_page: number
  review_school_error_ids: number[]
  surah_memorize_from_id: {
    name: string
    id: number
  }
  surah_memorize_to_id: {
    name: string
    id: number
  }
  surah_recitation_from_id: {
    name: string
    id: number
  }
  verse_recitation_from: number
  surah_recitation_to_id: {
    name: string
    id: number
  }
  verse_recitation_to: number
  total_recitation_line: number
  total_recitation_page: number
  recitation_school_error_ids: number[]
  create_date: string
  degree_memorize: string | false
  degree_review: string | false
}

export type Surah = {
  name: string
  id: number
  surah_number: number
}

export type TodayPlanWork = {
  plan_id: number
  plan_name: string
  line_count: number
  page_quantity: number
  surah_from_id: Surah | false
  verse_from: number | false
  surah_to_id: Surah | false
  verse_to: number | false
}

export type TodayPlan = {
  memorization: TodayPlanWork | false
  review: TodayPlanWork | false
}

export type PlanProgressItem = {
  plan_id: number
  plan_name: string
  type: 'memorize' | 'review'
  daily_page_quantity: 0.5 | 1 | 1.5 | 2 | 2.5 | 3 | 3.5 | 4 | 4.5 | 5
  completion_percentage: number
  status: 'not_started' | 'behind' | 'on_track' | 'ahead'
  current:
    | {
        surah_id: Surah
        verse: number
      }
    | false
}

export type StudentPlanProgress = {
  memorization: PlanProgressItem | false
  review: PlanProgressItem | false
}

export type PlanLine = {
  id: number
  surah_from_id: Surah
  verse_from: number
  surah_to_id: Surah
  verse_to: number
  date: string
  old_date: false | string
  evaluation_id: false | number
}

export type Entity = {
  id: number
  name: string
}

export type Plan = {
  id: number
  student_id: Entity
  date_start: string
  mosque_id: Entity
  memorize_plan_line_ids: PlanLine[]
  revue_plan_line_ids: PlanLine[]
}

export type Absence = {
  id: number
  student_id: Entity
  date: string
  reason: string
  absence_type: false | string
  date_start_absence: string
  mosque_id: Entity
  state: 'draft' | 'done'
}

export type TicketAttachment = {
  id: number
  name: string
  mimetype: string
  secure_link: string | false
}

export type Ticket = {
  id: number
  stage_id: {
    id: number
    name: string
  }
  name: string
  description: string
  priority: '1' | '2' | '3'
  date_done: string | false
  create_date: string
  team_id: {
    id: number
    name: string
  }
  ticket_type_id: {
    id: number
    name: string
  }
  attachment_ids: TicketAttachment[]
}

export type CreateTicketPayload = {
  name: string
  description: string
  attachment_ids?: Array<{
    name: string
    datas: string
  }>
}

export type Notification = {
  id: number
  name: string
  subject: string
  content: string
  content_html: string | false
  date: string
  image: string | false
  action: string | false
  res_model: string | false
  res_id: string | false
  is_seen: boolean
  attachment_ids: any[]
}

export type Teacher = {
  id: number
  name: string | false
  nationality_id:
    | {
        id: number
        name: string
      }
    | false
  email: string | false
  mobile: string | false
  enabled?: boolean
  active?: boolean
  user_id:
    | {
        id: number
        active: boolean
      }
    | false
}
